import { useUser } from '@clerk/react';
import { EnvelopeSimple } from '@phosphor-icons/react';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import { Badge, Button, Field, TextInput, toast } from '@reelplicate/ui';
import { type FormEvent, useState } from 'react';

import { ProfilePhoto } from './profile-photo';
import {
  SettingsCard,
  SettingsPageLayout,
  SettingsRow,
  SettingsSection,
} from './settings-layout';
import { SignedInSessions } from './signed-in-sessions';

/**
 * Figma "Settings — Account & security" (323:1418): your own profile and
 * sign-in. Name and photo come from Clerk and save back to it; the
 * email is shown but changed through a confirmation flow that isn't built
 * yet. Sign-in methods and two-factor reflect the Clerk user; sessions come
 * from the API.
 */
export function AccountPage() {
  const { user } = useUser();
  const { data: workspaces } = useListWorkspaces();
  // The name being edited; null until the user types, so Clerk's value shows.
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const savedName = user?.fullName ?? '';
  const name = draft ?? savedName;

  const email = user?.primaryEmailAddress?.emailAddress ?? '';
  const google = user?.externalAccounts?.find((a) => a.provider === 'google');
  const github = user?.externalAccounts?.find((a) => a.provider === 'github');
  const dirty = name.trim() !== savedName && name.trim() !== '';

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user || !dirty) return;
    const [firstName = '', ...rest] = name.trim().split(/\s+/);
    setSaving(true);
    try {
      await user.update({ firstName, lastName: rest.join(' ') });
      setDraft(null);
      toast.success('Profile saved');
    } catch {
      toast.error('Couldn’t save your profile. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsPageLayout
      title="Account & security"
      description="Your own profile and sign-in. These settings follow you across every workspace."
      onSubmit={(event) => void onSubmit(event)}
      actions={
        <Button type="submit" disabled={!dirty} loading={saving}>
          Save changes
        </Button>
      }
    >
      <SettingsSection
        title="Profile"
        description="How teammates see you in comments, reviews and the activity log."
      >
        <SettingsCard>
          <ProfilePhoto name={savedName || email} />
          <div className="flex flex-col gap-4 pt-5 pb-6 sm:flex-row sm:items-start">
            <Field label="Full name" className="flex-1">
              <TextInput
                value={name}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="e.g. Maya Kowalski"
              />
            </Field>
            <Field
              label="Email"
              helper="Changing it sends a confirmation link to the new address."
              className="flex-1"
            >
              <TextInput
                value={email}
                readOnly
                leadingIcon={EnvelopeSimple}
                placeholder="you@company.com"
              />
            </Field>
          </div>
        </SettingsCard>
      </SettingsSection>

      <SettingsSection title="Sign-in" description="Ways you can get into Reelplicate.">
        <SettingsCard>
          <SettingsRow
            label="Password"
            description={
              user?.passwordEnabled
                ? 'Used with your email to sign in.'
                : 'No password set.'
            }
            control={
              <Button type="button" variant="outline" size="sm">
                {user?.passwordEnabled ? 'Change password' : 'Set password'}
              </Button>
            }
          />
          <SettingsRow
            label="Google"
            description={
              google
                ? `Signed in with Google as ${google.emailAddress}.`
                : 'Not connected.'
            }
            control={
              <Button type="button" variant="outline" size="sm">
                {google ? 'Disconnect' : 'Connect'}
              </Button>
            }
          />
          <SettingsRow
            label="GitHub"
            description={
              github
                ? `Signed in with GitHub as ${github.emailAddress}.`
                : 'Not connected.'
            }
            control={
              <Button type="button" variant="outline" size="sm">
                {github ? 'Disconnect' : 'Connect'}
              </Button>
            }
          />
        </SettingsCard>
      </SettingsSection>

      <SettingsSection
        title="Two-factor authentication"
        description="A code from your authenticator app is asked for at every new sign-in."
      >
        <SettingsCard>
          <SettingsRow
            label="Authenticator app"
            badge={
              user?.totpEnabled ? <Badge tone="success">On</Badge> : <Badge>Off</Badge>
            }
            description={
              user?.totpEnabled
                ? 'Codes come from your authenticator app.'
                : 'Add an app like 1Password or Google Authenticator.'
            }
            control={
              <Button type="button" variant="outline" size="sm">
                {user?.totpEnabled ? 'Remove' : 'Set up'}
              </Button>
            }
          />
          <SettingsRow
            label="Recovery codes"
            description="Use one if you lose your phone."
            disabled={!user?.backupCodeEnabled}
            control={
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!user?.backupCodeEnabled}
              >
                View codes
              </Button>
            }
          />
        </SettingsCard>
      </SettingsSection>

      <SignedInSessions />

      <SettingsSection
        title="Danger zone"
        description="These actions can’t be undone."
        danger
      >
        <SettingsCard danger>
          <SettingsRow
            label="Delete my account"
            description={`You own ${workspaces?.[0]?.name ?? 'a workspace'}. Transfer ownership first, then you can delete your account.`}
            disabled
            control={
              <Button type="button" variant="outline" size="sm" disabled>
                Delete account
              </Button>
            }
          />
        </SettingsCard>
      </SettingsSection>
    </SettingsPageLayout>
  );
}
